import { useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import EditNote from "@mui/icons-material/EditNote";
import LinkOff from "@mui/icons-material/LinkOff";
import Refresh from "@mui/icons-material/Refresh";
import UploadFile from "@mui/icons-material/UploadFile";
import Visibility from "@mui/icons-material/Visibility";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useQueueManifestDocumentParse } from "hooks/useQueueManifestDocumentParse";
import { useState } from "react";

import { DELETE_DOCUMENT_MANIFEST, PROCESSED_MANIFESTS_QUERY } from "./api";
import { DocumentManifestEditDialog } from "./DocumentManifestEditDialog";
import { ManifestUploadDialog } from "./ManifestUploadDialog";

type Manifest = ProcessedManifestsQuery["processedManifests"]["items"][number];

type DocumentSourceActionsMenuProps = {
  manifest: Manifest;
  tenantId: string;
  onDocumentDrawerOpen: () => void;
  onManifestClose: () => void;
};

export function DocumentSourceActionsMenu({
  manifest,
  tenantId,
  onDocumentDrawerOpen,
  onManifestClose,
}: DocumentSourceActionsMenuProps) {
  const alerts = useAlerts();
  const {
    queueManifestDocumentParse,
    loading: reparseLoading,
    canQueueManifestParse,
  } = useQueueManifestDocumentParse();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showReparseConfirm, setShowReparseConfirm] = useState(false);
  const [showDetachConfirm, setShowDetachConfirm] = useState(false);
  const [showDeleteDocConfirm, setShowDeleteDocConfirm] = useState(false);
  const [showDocumentManifestEdit, setShowDocumentManifestEdit] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [deleteDocumentManifest, { loading: deletingManifest }] =
    useMutation(DELETE_DOCUMENT_MANIFEST);
  const [deleteDocumentMutation, { loading: deletingDocument }] = useDeleteDocumentMutation({
    refetchQueries: [PROCESSED_MANIFESTS_QUERY],
  });

  if (!canQueueManifestParse) return null;

  // No source document — show upload button
  if (!manifest.sourceDocument) {
    return (
      <>
        <Tooltip title="Upload manifest document">
          <IconButton size="small" onClick={() => setUploadDialogOpen(true)}>
            <UploadFile />
          </IconButton>
        </Tooltip>
        {uploadDialogOpen && (
          <ManifestUploadDialog
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            tenantId={tenantId}
            facilityId={manifest.facility?.id}
            manifestTrackingNumber={manifest.manifestTrackingNumber ?? undefined}
            generatorId={manifest.epaId ?? undefined}
          />
        )}
      </>
    );
  }

  // Has source document — show actions menu
  const closeMenu = () => setMenuAnchor(null);

  return (
    <>
      <Tooltip title="Document actions">
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <DescriptionOutlined />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            onDocumentDrawerOpen();
            closeMenu();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Source Document</ListItemText>
        </MenuItem>
        {manifest.documentManifestId && (
          <MenuItem
            onClick={() => {
              setShowDocumentManifestEdit(true);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <EditNote fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Extraction Data</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          disabled={reparseLoading}
          onClick={() => {
            setShowReparseConfirm(true);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>Re-parse Document</ListItemText>
        </MenuItem>
        {manifest.documentManifestId && (
          <MenuItem
            onClick={() => {
              setShowDetachConfirm(true);
              closeMenu();
            }}
            sx={{ color: "warning.main" }}
          >
            <ListItemIcon>
              <LinkOff fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Detach Manifest Data</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setShowDeleteDocConfirm(true);
            closeMenu();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Document</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit extraction data dialog */}
      {manifest.documentManifestId && (
        <DocumentManifestEditDialog
          open={showDocumentManifestEdit}
          onClose={() => setShowDocumentManifestEdit(false)}
          wasteManifestId={manifest.documentManifestId}
          sourceDocumentId={manifest.sourceDocument?.id}
        />
      )}

      {/* Re-parse confirmation */}
      <ConfirmDialog
        open={showReparseConfirm}
        onClose={() => setShowReparseConfirm(false)}
        onConfirm={async () => {
          if (!manifest.sourceDocument) return;
          try {
            const jobs = await queueManifestDocumentParse({
              tenantId,
              documentIds: [manifest.sourceDocument.id],
              facilityId: manifest.facility?.id,
              manifestTrackingNumber: manifest.manifestTrackingNumber ?? undefined,
              generatorId: manifest.epaId ?? undefined,
            });
            const jobId = jobs[0]?.job.id;
            alerts.success(
              <>
                Manifest re-parse queued.{" "}
                {jobId && (
                  <a href={`/staff/jobs/${jobId}`} target="_blank" rel="noreferrer">
                    View job
                  </a>
                )}
              </>
            );
          } catch (err) {
            alerts.error("Failed to queue re-parse.", err instanceof Error ? err : undefined);
          }
          setShowReparseConfirm(false);
        }}
        header="Re-parse manifest document?"
        info={
          <>
            This will re-extract data from the source document and replace the existing manifest
            data. Any changes made since the last parse will be overwritten.
            {(manifest.manifestTrackingNumber || manifest.epaId) && (
              <>
                <br />
                <br />
                The following values will be preserved during re-parse:
                {manifest.manifestTrackingNumber && (
                  <>
                    <br />
                    <strong>MTN:</strong> {manifest.manifestTrackingNumber}
                  </>
                )}
                {manifest.epaId && (
                  <>
                    <br />
                    <strong>Generator ID:</strong> {manifest.epaId}
                  </>
                )}
              </>
            )}
          </>
        }
        confirmText="Re-parse"
        loading={reparseLoading}
      />

      {/* Detach manifest data confirmation */}
      <ConfirmDialog
        open={showDetachConfirm}
        onClose={() => setShowDetachConfirm(false)}
        onConfirm={async () => {
          if (!manifest.documentManifestId) return;
          try {
            await deleteDocumentManifest({
              variables: { wasteManifestId: manifest.documentManifestId },
              refetchQueries: [PROCESSED_MANIFESTS_QUERY],
            });
            alerts.success("Manifest data detached. The source document has been kept.");
          } catch (err) {
            alerts.error("Failed to detach manifest data.", err instanceof Error ? err : undefined);
          }
          setShowDetachConfirm(false);
        }}
        header="Detach manifest data?"
        info="This will delete the extracted manifest data (waste manifest, waste lines, and transporters) but keep the source document. The document can be re-parsed later."
        confirmText="Detach"
        loading={deletingManifest}
      />

      {/* Delete document confirmation */}
      <ConfirmDialog
        open={showDeleteDocConfirm}
        onClose={() => setShowDeleteDocConfirm(false)}
        onConfirm={async () => {
          if (!manifest.sourceDocument) return;
          try {
            // Detach manifest data first (FK is RESTRICT, so document delete
            // would fail if manifest records still reference it)
            if (manifest.documentManifestId) {
              await deleteDocumentManifest({
                variables: { wasteManifestId: manifest.documentManifestId },
              });
            }
            await deleteDocumentMutation({
              variables: {
                id: manifest.sourceDocument.id,
                retainAsset: false,
              },
            });
            alerts.success("Document and associated manifest data deleted.");
            onManifestClose();
          } catch (err) {
            alerts.error("Failed to delete document.", err instanceof Error ? err : undefined);
          }
          setShowDeleteDocConfirm(false);
        }}
        header="Delete document?"
        info="This will permanently delete the source document and all associated manifest data (waste manifest, waste lines, and transporters). This cannot be undone."
        confirmText="Delete"
        loading={deletingDocument}
      />
    </>
  );
}
